/**
 * Reusable light rig presets extracted from existing level scenes.
 *
 * Each preset defines ambient + key light + accent lights that together
 * create a distinct lighting mood. Levels compose a VisualProfile by
 * picking a preset and optionally overriding individual properties.
 *
 * Preset names are descriptive so a non-game-dev can choose one:
 *   - "warm interior" = cozy indoor scene (Liminal Atrium)
 *   - "twilight forest" = dusk in a dense forest (Redwood Scrapyard)
 *   - "underground bakery" = warm underground with candles (Midnight Burrow)
 *   - "starlit canopy" = cool nighttime treetops (Clockwork Canopy)
 *   - "dark menu" = minimal lighting for menu screens (TitleScreen)
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { LightRigPreset } from "../VisualProfile";
import { COLORS } from "../../config/GameConfig";

// ── Warm Interior ──────────────────────────────────────────────────────
// Source: LiminalAtrium.ts
// Moody Victorian sunroom with golden sunbeam and warm candle accents.
// Ambient pushed warmer and brighter than the previous cool-purple tone
// so the loaded Victorian furniture in atrium_env.glb actually reads at
// runtime — the old values made the scene look like a pitch-black void.

export const WARM_INTERIOR: LightRigPreset = {
  ambient: {
    color: new Color3(0.35, 0.28, 0.40),       // Warmer interior bounce
    specular: new Color3(0.12, 0.11, 0.14),
    groundColor: new Color3(0.12, 0.08, 0.06), // Warm floor bounce (mahogany)
    intensity: 1.35,
  },
  key: {
    direction: new Vector3(-0.45, -1, 0.25),   // Steeper god-ray angle
    color: new Color3(1.0, 0.82, 0.55),         // Warm golden sunbeam
    specular: new Color3(1.0, 0.9, 0.6),
    intensity: 3.2,
    shadowEnabled: true,
  },
  accents: [
    // Candle warm points (3 positions from Atrium)
    {
      position: new Vector3(-6, 3, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.6,
      range: 8,
    },
    {
      position: new Vector3(3, 4, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.6,
      range: 8,
    },
    {
      position: new Vector3(9, 5, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.6,
      range: 8,
    },
  ],
};

// ── Twilight Forest ────────────────────────────────────────────────────
// Source: RedwoodScrapyard.ts (createLighting)
// Muted forest light filtered through a dense canopy at dusk.

export const TWILIGHT_FOREST: LightRigPreset = {
  ambient: {
    color: new Color3(0.18, 0.15, 0.25),       // Muted forest
    specular: new Color3(0.08, 0.06, 0.1),
    groundColor: new Color3(0.06, 0.04, 0.08),
    intensity: 1.0,
  },
  key: {
    direction: new Vector3(-0.3, -1, 0.4),     // Filtered forest sunlight
    color: new Color3(0.85, 0.65, 0.35),        // Warm amber through canopy
    specular: new Color3(0.7, 0.5, 0.3),
    intensity: 2.0,
    shadowEnabled: true,
  },
  accents: [
    // Candle-like warm points scattered among the scrapyard (6 positions)
    {
      position: new Vector3(10, 4, -1),
      color: new Color3(1.0, 0.55, 0.15),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.45,
      range: 10,
    },
    {
      position: new Vector3(35, 5, -1),
      color: new Color3(1.0, 0.55, 0.15),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.45,
      range: 10,
    },
    {
      position: new Vector3(60, 3, -1),
      color: new Color3(1.0, 0.55, 0.15),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.45,
      range: 10,
    },
    {
      position: new Vector3(85, 6, -1),
      color: new Color3(1.0, 0.55, 0.15),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.45,
      range: 10,
    },
    {
      position: new Vector3(110, 4, -1),
      color: new Color3(1.0, 0.55, 0.15),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.45,
      range: 10,
    },
    {
      position: new Vector3(140, 5, -1),
      color: new Color3(1.0, 0.55, 0.15),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.45,
      range: 10,
    },
  ],
};

// ── Underground Bakery ─────────────────────────────────────────────────
// Source: OvergrownConservatory.ts
// Warm oven glow with candle accents and bioluminescent fungi.
// Uses the DAY phase lighting values as the default state.

export const UNDERGROUND_BAKERY: LightRigPreset = {
  ambient: {
    color: Color3.FromHexString('#8b7860'),     // DAY.ambient
    specular: new Color3(0.08, 0.06, 0.1),
    groundColor: Color3.FromHexString('#1a0f08'), // BURROW_DEEP
    intensity: 1.2,                               // DAY.intensity
  },
  key: {
    direction: new Vector3(-0.4, -1, 0.3),      // Warm oven glow from above-left
    color: Color3.FromHexString('#ffe8cc'),       // DAY.directional
    specular: new Color3(0.8, 0.7, 0.5),
    intensity: 1.8,                               // DAY.intensity * 1.5
    shadowEnabled: true,
  },
  accents: [
    // Candle accent lights (6 underground bakery positions)
    {
      position: new Vector3(-10, 4, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 10,
    },
    {
      position: new Vector3(5, 5, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 10,
    },
    {
      position: new Vector3(20, 4, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 10,
    },
    {
      position: new Vector3(35, 6, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 10,
    },
    {
      position: new Vector3(50, 3, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 10,
    },
    {
      position: new Vector3(65, 5, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 10,
    },
    // Bioluminescent fungi accent
    {
      position: new Vector3(-15, 3, -2),
      color: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
      intensity: 0.5,
      range: 14,
    },
    // Escort corridor (x=85..155) cool-dim moonlit accents. Canon L2
    // escort is a midnight warren — the bakery candles only cover x<65
    // and the L2 visual QA flagged a full blackout through the escort
    // section, which is exactly the section that needs readable cover
    // for fox sight lines and opossum chains. Keep the palette cool
    // (midnight indigo rather than candle amber) so the corridor reads
    // "underground at night, not lit kitchen."
    {
      position: new Vector3(85, 4, -1),
      color: new Color3(0.38, 0.42, 0.6),
      intensity: 0.7,
      range: 12,
    },
    {
      position: new Vector3(100, 5, -1),
      color: new Color3(0.38, 0.42, 0.6),
      intensity: 0.7,
      range: 12,
    },
    {
      position: new Vector3(115, 4, -1),
      color: new Color3(0.38, 0.42, 0.6),
      intensity: 0.7,
      range: 12,
    },
    {
      position: new Vector3(130, 6, -1),
      color: new Color3(0.38, 0.42, 0.6),
      intensity: 0.7,
      range: 12,
    },
    {
      position: new Vector3(145, 5, -1),
      color: new Color3(0.38, 0.42, 0.6),
      intensity: 0.7,
      range: 12,
    },
    // Warm amber hearth accent near Elder Gus (x≈155) — his encounter
    // area reads as "the safe warm kitchen at the end of the warren"
    // so the final beat tonal-shifts back to candle amber.
    {
      position: new Vector3(155, 8, -1),
      color: new Color3(1.0, 0.6, 0.2),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 12,
    },
  ],
};

// ── Starlit Canopy ─────────────────────────────────────────────────────
// Source: ClockworkCanopy.ts (createLighting)
// Cool moonlit treetops with warm brass observatory accents.

export const STARLIT_CANOPY: LightRigPreset = {
  ambient: {
    color: new Color3(0.1, 0.12, 0.2),         // Cool starlit
    specular: new Color3(0.05, 0.05, 0.1),
    groundColor: new Color3(0.03, 0.02, 0.06),
    intensity: 1.0,
  },
  key: {
    direction: new Vector3(-0.3, -1, 0.2),     // Moonlight
    color: new Color3(0.5, 0.55, 0.7),          // Cool blue-white
    specular: new Color3(0.3, 0.35, 0.5),
    intensity: 1.8,
    shadowEnabled: true,
  },
  accents: [
    // Warm brass accent lights along the canopy (5 positions)
    {
      position: new Vector3(15, 6, -1),
      color: Color3.FromHexString('#c49b3c'),   // BRASS_WARM
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 12,
    },
    {
      position: new Vector3(40, 8, -1),
      color: Color3.FromHexString('#c49b3c'),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 12,
    },
    {
      position: new Vector3(70, 6, -1),
      color: Color3.FromHexString('#c49b3c'),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 12,
    },
    {
      position: new Vector3(95, 5, -1),
      color: Color3.FromHexString('#c49b3c'),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 12,
    },
    {
      position: new Vector3(125, 8, -1),
      color: Color3.FromHexString('#c49b3c'),
      specular: Color3.FromHexString(COLORS.BRASS),
      intensity: 0.5,
      range: 12,
    },
  ],
};

// ── Dark Menu ──────────────────────────────────────────────────────────
// Source: TitleScreen.ts
// Minimal lighting for menu screens: deep purple ambient with
// bioluminescent and copper accent points.

export const DARK_MENU: LightRigPreset = {
  ambient: {
    color: Color3.FromHexString(COLORS.DEEP_PURPLE),
    specular: new Color3(0.05, 0.05, 0.08),
    groundColor: Color3.FromHexString(COLORS.MIDNIGHT_BLUE),
    intensity: 0.3,
  },
  key: {
    // Menu has no directional key light -- use a very dim one pointing down
    direction: new Vector3(0, -1, 0),
    color: new Color3(0.1, 0.08, 0.15),
    specular: new Color3(0.05, 0.04, 0.08),
    intensity: 0.1,
    shadowEnabled: false,
  },
  accents: [
    {
      position: new Vector3(-5, 3, -5),
      color: Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN),
      intensity: 0.4,
      range: 12,
    },
    {
      position: new Vector3(5, -2, -5),
      color: Color3.FromHexString(COLORS.COPPER),
      intensity: 0.3,
      range: 12,
    },
  ],
};
